import React, { useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import CharityStackWordmark from '../assets/CharityStack Text.png';

const DEFAULT_TITLE = "We're upgrading our systems";
const DEFAULT_MESSAGE =
  'Our team is working on improvements to give you a better experience. Please check back shortly.';

function parsePayload(raw) {
  if (!raw) return { title: DEFAULT_TITLE, message: DEFAULT_MESSAGE, estimatedReturn: null, showContactSupport: true };
  return {
    title: raw.title ?? DEFAULT_TITLE,
    message: raw.message ?? DEFAULT_MESSAGE,
    estimatedReturn: raw.estimatedReturn != null ? raw.estimatedReturn : null,
    showContactSupport: raw.showContactSupport !== false,
  };
}

function MaintenancePage() {
  const posthog = usePostHog();

  // Subscribe to flag payload updates so messaging refreshes live
  // when an operator changes the ETA or message in PostHog mid-incident.
  const [{ title, message, estimatedReturn, showContactSupport }, setPayload] = useState(() => {
    try { return parsePayload(posthog?.getFeatureFlagPayload?.('dashboard_maintenance_mode')); }
    catch { return parsePayload(null); }
  });

  useEffect(() => {
    if (!posthog) return;
    const unsub = posthog.onFeatureFlags(() => {
      try { setPayload(parsePayload(posthog.getFeatureFlagPayload('dashboard_maintenance_mode'))); }
      catch { /* keep current values */ }
    });
    return () => unsub?.();
  }, [posthog]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FCFBF8 0%, #F0ECE4 100%)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        padding: '24px',
      }}
    >
      <div
        style={{
          maxWidth: '520px',
          width: '100%',
          textAlign: 'center',
          padding: '48px 32px',
          backgroundColor: '#FCFBF8',
          borderRadius: '12px',
          border: '1px solid #E8E4DC',
          boxShadow: '0 1px 3px rgba(58, 52, 41, 0.06)',
        }}
      >
        {/* Logo */}
        <img
          src={CharityStackWordmark}
          alt="CharityStack"
          style={{
            height: '32px',
            margin: '0 auto 40px',
            display: 'block',
          }}
        />

        {/* Gear icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#EDF3ED',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 28px',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6B8E6B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
            <path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
            <path d="M12 2v2" />
            <path d="M12 20v2" />
            <path d="m4.93 4.93 1.41 1.41" />
            <path d="m17.66 17.66 1.41 1.41" />
            <path d="M2 12h2" />
            <path d="M20 12h2" />
            <path d="m6.34 17.66-1.41 1.41" />
            <path d="m19.07 4.93-1.41 1.41" />
          </svg>
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 600,
            color: '#3A3429',
            margin: '0 0 12px',
            lineHeight: 1.3,
          }}
        >
          {String(title)}
        </h1>

        {/* Message */}
        <p
          style={{
            fontSize: '15px',
            color: '#6B6458',
            margin: '0 0 8px',
            lineHeight: 1.6,
          }}
        >
          {String(message)}
        </p>

        {/* Estimated return */}
        {estimatedReturn && (
          <p
            style={{
              fontSize: '14px',
              color: '#6B8E6B',
              fontWeight: 500,
              margin: '16px 0 0',
              padding: '10px 16px',
              backgroundColor: '#EDF3ED',
              borderRadius: '8px',
              display: 'inline-block',
            }}
          >
            Estimated return: {String(estimatedReturn)}
          </p>
        )}

        {/* Contact support */}
        {showContactSupport && (
          <>
            <div
              style={{
                height: '1px',
                backgroundColor: '#E8E4DC',
                margin: '32px 0 24px',
              }}
            />
            <p
              style={{
                fontSize: '13px',
                color: '#9A9285',
                margin: 0,
              }}
            >
              Need help?{' '}
              <a
                href="mailto:support@charitystack.com"
                style={{
                  color: '#6B8E6B',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                Contact Support
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default MaintenancePage;
